<template>
  <article
    class="resource"
    :class="{
      'is-showing-details': shouldShowDetails,
      'should-show-full-title': $settings.resources.showFullTitle,
      'is-pinned': isPinned,
      'has-spacing-horizontal': spacingHorizontal
    }"
  >
    <div v-if="isPinned" class="resource__pin">
      <nice-icon
        type="fas"
        icon="thumbtack"
        class="resource__pin-icon"
      ></nice-icon>
    </div>
    <div class="resource__top">
      <div class="resource__title-wrap" style="flex:1; min-width:0;">
        <a
          :href="resource.url"
          target="_blank"
          rel="noopener noreferrer"
          class="resource__title"
          :title="resource.url"
          @click="onLinkClick"
        >
          <span
            class="resource__favicon"
            v-if="$settings.resources.showFavicon"
          >
            <img
              :src="
                `${$env.VUE_APP_BACKEND_URL}storage/favicons/${faviconBase64}.png`
              "
              alt="favicon of link"
            />
          </span>
          <span>{{ resource.name }}</span></a
        >

        <b-tooltip :label="isPublic ? 'Public' : 'Private'" position="is-right">
          <nice-icon
            v-if="isPublic"
            spacing-left
            icon="globe-europe"
            class="resource-public"
          ></nice-icon>
          <nice-icon
            v-else
            spacing-left
            icon="lock-alt"
            class="resource-private"
          ></nice-icon>
        </b-tooltip>
      </div>

      <div class="resource__controls">
        <button
          v-show="!forceShowTags"
          class="resource__details-button"
          @click="showTags = !showTags"
          :class="{ 'is-active': showTags }"
        >
          <nice-icon icon="tags" :fixed-width="false"></nice-icon>
        </button>

        <button
          v-show="!forceShowDetails"
          class="resource__details-button"
          @click="showDetails = !showDetails"
          :class="{ 'is-active': showDetails }"
        >
          <nice-icon icon="info-circle" :fixed-width="false"></nice-icon>
        </button>

        <b-dropdown position="is-bottom-left" animation="pop">
          <button class="resource__options-button" slot="trigger">
            <nice-icon icon="ellipsis-v" :fixed-width="false"></nice-icon>
          </button>

          <b-dropdown-item
            class="clipboard-button"
            :data-clipboard-text="resource.url"
          >
            <nice-icon icon="copy" spacing-right></nice-icon>
            <span>{{ $t("buttons.copyURL") }}</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="!currentUserIsOwner" @click="copyResource">
            <nice-icon icon="copy" spacing-right></nice-icon>
            <span>Copy to own resources</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="!currentContextIsExplore && currentUserIsOwner"
            @click="togglePin"
          >
            <nice-icon icon="thumbtack" spacing-right></nice-icon>
            <span v-if="!resource.is_pinned">Pin to top</span>
            <span v-else>Unpin</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="currentUserIsOwner" @click="openShareModal">
            <nice-icon icon="share" spacing-right></nice-icon>
            <span>{{ $t("buttons.share") }}</span>
          </b-dropdown-item>

          <div class="dropdown-divider" v-if="currentUserIsOwner"></div>

          <b-dropdown-item
            v-if="currentUserIsOwner"
            @click="$router.push(`/resource/${resource.id}`)"
          >
            <nice-icon icon="pencil" spacing-right></nice-icon>
            <span>{{ $t("buttons.edit") }}</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="currentUserIsOwner" @click="deleteResource">
            <nice-icon icon="trash-alt" spacing-right></nice-icon>
            <span>{{ $t("buttons.deleteResource") }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <div class="resource__sub" v-show="shouldShowDetails">
      <div
        class="resource__comment mb2 flex items-center"
        v-if="resource.comment && !$utils.isEmpty(resource.comment)"
      >
        <nice-icon icon="comment-alt-lines" spacing-right></nice-icon>
        <span>{{ resource.comment }}</span>
      </div>
      <div class="flex items-center mb2">
        <user
          v-if="showUser"
          :user="resource.user"
          is-link
          show-name
          class="mr3"
        ></user>

        <div class="resource__icon">
          <nice-icon icon="clock"></nice-icon>
        </div>

        <b-tooltip
          :label="new Date(resource.created_at).toLocaleString()"
          type="is-dark"
          position="is-right"
          offset="0"
          class="mr3"
        >
          <time>{{ new Date(resource.created_at).toLocaleDateString() }}</time>
        </b-tooltip>

        <img v-if="resource.ico_link" :src="resource.ico_link" alt="Icon" />
        <div v-else class="resource__icon">
          <nice-icon icon="external-link"></nice-icon>
        </div>
        <a
          class="resource__url"
          :href="resource.url"
          target="_blank"
          @click="onLinkClick"
          >{{ resource.url }}</a
        >
      </div>

      <div class="flex items-center">
        <nice-icon icon="eye" spacing-right></nice-icon>
        <span>{{ ownClicksText }}</span>
        <span v-if="isPublic" class="ml2">{{ foreignClicksText }}</span>
      </div>
    </div>

    <!-- v-if="resource.tags && resource.tags.length > 0" -->
    <div class="tag__container" v-show="shouldShowTags">

      <tag-list
        :tags="resource.tags"
        :resourceId="resource.id"
        :contextOptions="
          Object.assign({}, contextOptions, { userIsOwner: currentUserIsOwner })
        "
        :tag-props="{ emitGlobalClickEvent: true }"
      >
        <template v-slot:afterTags>
          <b-dropdown
            :key="`resource-${resource.id}-add-tags`"
            animation="pop"
            class="add-tags-dropdown"
            v-if="!isReadOnly"
            @active-change="onShowTagDropdown"
          >
            <button slot="trigger" class="add-tags-button ph2 mb2 is-muted">
              <div class="flex items-center justify-center">
                <nice-icon icon="plus"></nice-icon>
                <span
                  class="ml2"
                  v-if="!resource.tags || resource.tags.length < 1"
                  >{{ $t("buttons.addTags") }}</span
                >
              </div>
            </button>

            <b-dropdown-item paddingless custom :focusable="false">
              <advanced-tag-list
                v-if="tagListVisible"
                ref="otherTagList"
                :use="useTags"
                :exclude="existingTagIds"
                @tagClicked="addTag"
                @beforeTagCreate="onBeforeTagCreate"
                @errorTagCreate="onErrorTagCreate"
                :show-all="false"
                :is-in-dropdown="true"
                :show-context-divider="false"
                :tag-props="{ canGrow: true }"
                class="pa2"
                :chunk="25"
                :context="context"
              ></advanced-tag-list>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </tag-list>
    </div>
    <!-- <div class="resource__divider"></div> -->
  </article>
</template>

<script>
import TagList from "@/shared/components/tag/SimpleList";
import AdvancedTagList from "@/shared/components/tag/AdvancedList";

export default {
  props: {
    resource: {
      type: Object,
      required: true
    },
    showUser: {
      type: Boolean,
      default: false
    },
    forceShowDetails: {
      type: Boolean,
      default: false
    },
    forceShowTags: {
      type: Boolean,
      default: false
    },
    showPinned: {
      type: Boolean,
      default: true
    },
    tagListProps: {
      type: Object
    },
    availableTags: {
      type: Array
    },
    context: {
      type: String,
      required: true
    },
    spacingHorizontal: {
      type: Boolean,
      default: true
    },
    contextOptions: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      isEditMode: null,
      newText: "",
      addTagFilterText: "",
      showDetails: false,
      showTags: false,
      shareModalVisible: false,

      tagListVisible: false
    };
  },

  methods: {
    togglePin() {
      this.$store.dispatch(`${this.currentContext}/updateResource`, {
        resource: Object.assign({}, this.resource, {
          is_pinned: !this.resource.is_pinned
        })
      });
    },

    onLinkClick() {
      this.$store.dispatch("addResourceClick", this.resource);

      const newClicks = this.resource.own_clicks + 1;

      if (this.resource.user_id == this.$user.id) {
        this.$store.commit(`${this.context}/UPDATE_RESOURCE`, {
          id: this.resource.id,
          data: {
            own_clicks: newClicks
          }
        });
      }

      if (this.context !== "own") return;

      let mostClickedIndex = null;
      for (const [index, r] of this.$store.getters[
        "own/mostClickedResources"
      ].entries()) {
        if (mostClickedIndex === null && newClicks >= r.own_clicks) {
          mostClickedIndex = index;
          break;
        }
      }

      if (mostClickedIndex) {
        const existingIndex = this.$store.state.own.mostClickedResourcesIds.findIndex(
          id => id == this.resource.id
        );

        if (existingIndex > -1) {
          if (existingIndex == mostClickedIndex) return;

          this.$store.commit("own/MOVE_MOST_CLICKED_RESOURCE", {
            fromIndex: existingIndex,
            toIndex: mostClickedIndex
          });
        } else {
          this.$store.commit("own/INSERT_MOST_CLICKED_RESOURCE", {
            resource: this.resource,
            index: mostClickedIndex
          });
          this.$store.commit("own/POP_MOST_CLICKED_RESOURCE");
        }
      }
    },

    openShareModal() {
      this.shareModalVisible = true;
      this.$buefy.toast.open({
        message: "Feature is coming soon",
        type: "is-warning"
      });
    },

    async addTag(tag) {
      this.onErrorTagCreate(tag);

      await this.$store.dispatch(`${this.context}/addTag`, {
        resource: this.resource,
        tag: tag
      });
    },
    onErrorTagCreate(tag) {
      this.$store.commit(`${this.context}/REMOVE_TAG_FROM_RESOURCE_BY_TEXT`, {
        resource: this.resource,
        tag
      });
    },
    onBeforeTagCreate(tag) {
      this.$store.commit(`${this.context}/ADD_TAG_TO_RESOURCE`, {
        resource: this.resource,
        tag
      });
    },

    deleteResource() {
      this.$buefy.dialog.confirm({
        animation: "pop-modal",
        type: "is-danger",
        hasIcon: true,
        title: "Delete resource",
        message: `Are you sure you want to delete <span class="i">"${
          this.resource.name.length > 50
            ? this.resource.name.substring(0, 50) + "..."
            : this.resource.name
        }"</span>?`,
        onConfirm: async () => {
          await this.$store.dispatch(`${this.context}/deleteResource`, {
            resource: this.resource
          });
        }
      });
    },

    copyResource() {
      this.$router.push({
        name: "resource",
        params: { id: this.resource.id },
        query: { mode: "copy" }
      });
    },

    onShowTagDropdown(active) {
      this.tagListVisible = active;
      if (active) {
        this.$nextTick(() => {
          this.$refs.otherTagList.focusInput();
        });
      }
    }
  },

  computed: {
    existingTagIds() {
      return this.resource.tags.map(t => t.id);
    },

    allTags() {
      return this.$store.getters[`${this.context}/tags`];
    },

    tagsArray() {
      return this.$store.getters[`${this.context}/tagsArray`];
    },
    tagsSortedByResourceCountAndText() {
      return this.$store.getters[
        `${this.context}/tagsSortedByResourceCountAndText`
      ];
    },

    faviconBase64() {
      const url = new URL(this.resource.url);
      return window.btoa(url.hostname);
    },

    isReadOnly() {
      return !this.$auth.check() || !this.currentUserIsOwner;
    },

    shouldShowDetails() {
      return this.forceShowDetails || this.showDetails;
    },
    shouldShowTags() {
      return this.forceShowTags || this.showTags;
    },

    useTags() {
      return this.availableTags || this.tagsSortedByResourceCountAndText;
    },

    isPublic() {
      return this.resource.is_public;
    },
    ownClicksText() {
      let text = "";

      switch (this.resource.own_clicks) {
        case 0:
          text += "Never opened";
          break;
        case 1:
          text += "Opened once";
          break;
        case 2:
          text += "Opened twice";
          break;
        default:
          text += `Opened ${this.resource.own_clicks} times`;
          break;
      }

      if (this.isPublic) {
        text = `${text} by you.`;
      } else {
        text += ".";
      }

      return text;
    },
    foreignClicksText() {
      let text = "";

      switch (this.resource.foreign_clicks) {
        case 0:
          text += "Never opened by others.";
          break;
        case 1:
          text += "Opened once by others.";
          break;
        case 2:
          text += "Opened twice by others.";
          break;
        default:
          text += `Opened ${this.resource.own_clicks} times by others.`;
          break;
      }

      return text;
    },

    isPinned() {
      return (
        this.resource.is_pinned &&
        this.currentContext !== "explore" &&
        this.showPinned
      );
    },

    currentUserIsOwner() {
      return (
        this.$isAuthenticated &&
        this.resource.user_id.toString() === this.$user.id.toString()
      );
    }
  },

  components: {
    TagList,
    AdvancedTagList
  }
};
</script>

<style lang="scss" scoped>
.resource {
  $block: &;
  user-select: none;
  padding: 1rem 0 1rem;
  transition: 100ms all;
  will-change: background;
  position: relative;

  &.has-spacing-horizontal {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @include tablet() {
    padding: 1.1rem 0 1.1rem;

    &.has-spacing-horizontal {
      padding-left: 2rem;
      padding-right: 2rem;
    }
  }

  &.has-spacing-horizontal {
    &:hover,
    &:focus-within {
      background: #1e2129;
    }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 19px;
    font-weight: 400;
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    display: block;
    margin-top: 0;
    margin-bottom: 0;
    color: $text;
    text-decoration: none;
    transition: $transition-hover;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &__sub {
    color: $light_40;
    margin-top: 15px;
    margin-bottom: 15px;
  }

  &__favicon {
    // padding: 3px;
    // background: $white;
    // border-radius: 50%;
    // padding-top: 1px;
    margin-right: 10px;
    display: inline-flex;
    height: 22px;
    width: 22px;
    background: $light_20;
    border-radius: 50%;
    padding: 4px;
    overflow: hidden;

    img {
      height: 100%;
      width: auto;
    }
  }

  &__url {
    text-decoration: none;
    font-size: 1rem;
    color: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &:hover {
      text-decoration: underline;
    }
  }

  &__icon {
    font-size: inherit;
    margin-right: 7px;
  }

  &__divider {
    height: 1px;
    width: 100%;
    background: $light_05;
    // margin-top: 20px;
    // margin-bottom: 25px;
    position: relative;
    bottom: -20px;

    @include tablet() {
      bottom: -20px;
    }
  }

  &__controls {
    flex-shrink: 0;
    margin-left: 0;
    color: $light_20;
    transition: 200ms all;
    flex-wrap: nowrap;
    white-space: nowrap;
    will-change: width;

    @include tablet() {
      opacity: 0;
      // width: 0;
    }
  }

  &__details-button {
    color: inherit;
    transition: inherit;
    font-size: 20px;
    padding: 2px 10px;

    &.is-active,
    &:hover {
      color: $text;
    }
  }

  &__options-button {
    color: inherit;
    transition: inherit;
    font-size: 20px;
    padding: 2px 10px;

    &:focus,
    &:active {
      color: $text;
    }

    &:hover {
      color: $text;
    }
  }

  &.is-pinned {
    padding-left: 60px;
  }

  &__pin {
    position: absolute;
    top: 25px;
    left: 32px;
    bottom: 25px;
    width: 5px;
    border-radius: 10px;
    background: $primary;

    &:before {
      content: "";
      position: absolute;
      display: block;
      background: $primary;
      border-radius: 50%;
      height: 24px;
      width: 24px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      font-size: 13px;
      display: block;
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }

  &:last-of-type .resource__divider {
    display: none;
  }

  &:hover,
  &:focus-within {
    // background-color: #292f44;

    #{$block}__controls {
      opacity: 1 !important;
      // width: 65px !important;
      // margin-left: 1rem;
    }
  }

  &.is-showing-details,
  &.should-show-full-title {
    #{$block}__title {
      overflow: visible;
      white-space: normal;
    }

    #{$block} {
      &__top {
        align-items: flex-start;
      }
      // &__title-wrap {
      //   align-items: flex-start;
      // }
    }
  }
}

.tag {
  &__container {
    margin: 10px 0 0 0;
    display: flex;
    flex-wrap: wrap;

    @include tablet() {
      margin: 10px 0 0 0;
    }
  }

  padding: 7px 13px;
  background: $light_05;
  border-radius: 3px;
  font-size: 15px;
  flex-grow: 0;
  transition: 200ms all;
  cursor: pointer;
  margin: 0 10px 10px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  &__text {
    color: $text;
    // opacity: 0.75;
    transition: inherit;
  }

  &__input {
    background: $dark;
    width: auto;
    border: none;
    color: inherit;
    padding: 0 5px;
  }

  &__options-button {
    background: none;
    border: none;
    color: inherit;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    right: 3px;
    bottom: 0;
    opacity: 0;
    transform: scale(0);
    transition: inherit;
    cursor: pointer;
    padding: 0 !important;
    color: $text;
  }

  &.has-options:hover {
    padding-right: 40px;
  }

  &:hover,
  &:focus-within {
    background: $light_05;

    .tag-text {
      opacity: 1;
    }

    .tag__options-button {
      opacity: 1;
      transform: scale(1);
      padding: 0 7px;
    }
  }
}

.is-muted {
  color: $light_20;
  transition: 200ms all;

  &:focus,
  &:active {
    opacity: 1;
    color: $text;
  }

  &:hover {
    color: $text;
  }
}

.username {
  color: $light_40;
}

.color-picker {
  // width: 238px !important;
  background: $dropdown-content-background-color !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.add-tags-dropdown {
  &.is-active {
    .add-tags-button {
      color: $text;
    }
  }
}

.add-tags-button {
  height: 34px;
  margin-left: -5px;
}

.resource-public {
  color: $primary;
}
.resource-private {
  color: $light_40;
}
</style>

<style lang="scss">
.tag__container .dropdown-menu {
  width: 350px;
  max-width: 90vw;

  .dropdown-content {
    padding-bottom: 20px !important;
  }
}
.color-picker .color-type {
  display: none !important;
}
</style>
